<template>
  <div class="group-chat" :class="viewClass">
    <!-- Header bar — always visible -->
    <div class="group-chat__header" @click="cycleState">
      <span
        class="group-chat__title"
        :class="{ 'group-chat__title--flash': newMessageFlash }"
      >
        {{ chatTitle }}
      </span>
      <q-space />
      <q-badge
        v-if="viewState !== 'expanded' && unreadCount > 0"
        color="red"
        floating
        :label="unreadCount"
        class="q-mr-sm"
      />
      <q-btn
        flat
        dense
        round
        :icon="headerIcon"
        size="sm"
        @click.stop="cycleState"
      />
    </div>

    <!-- Viewer role notice -->
    <div v-show="viewState !== 'collapsed'" class="group-chat__banner">
      This chat is not visible to users with &ldquo;viewer&rdquo; role
    </div>

    <!-- Messages + input — hidden when collapsed -->
    <div
      v-show="viewState !== 'collapsed'"
      ref="messageList"
      class="group-chat__messages"
      @scroll="onScroll"
    >
      <div v-if="loadingOlder" class="group-chat__loading">
        Loading older messages…
      </div>
      <div v-if="loading" class="group-chat__loading">Loading…</div>
      <div v-if="!loading && messages.length === 0" class="group-chat__empty">
        No chat history yet
      </div>
      <div
        v-for="msg in messages"
        :key="msg.id"
        class="group-chat__bubble"
        :class="{ 'group-chat__bubble--own': msg.userId === currentUserId }"
      >
        <div class="group-chat__meta">
          <span class="group-chat__author">{{ msg.userName }}</span>
          <span class="group-chat__time">{{ formatTime(msg.timestamp) }}</span>
          <q-btn
            v-if="msg.userId === currentUserId"
            flat
            dense
            round
            icon="delete_outline"
            size="xs"
            color="grey-6"
            class="group-chat__delete"
            @click="deleteMessage(msg.id)"
          />
        </div>
        <div class="group-chat__text">{{ msg.text }}</div>
      </div>

      <!-- Typing indicator -->
      <div v-if="someoneTyping" class="group-chat__typing">
        <span>Someone is typing</span>
        <span class="typing-dots">
          <span></span>
          <span></span>
          <span></span>
        </span>
      </div>
    </div>

    <div v-show="viewState !== 'collapsed'" class="group-chat__input">
      <q-input
        v-model="draft"
        dense
        outlined
        placeholder="Type a message…"
        class="group-chat__field"
        maxlength="2000"
        :error="isOverLimit"
        :error-message="`Message exceeds ${2000} characters`"
        hide-bottom-space
        @keydown.enter.prevent="send"
      >
        <template #append>
          <span
            v-if="draft.length > 1800"
            class="group-chat__char-count"
            :class="{ 'group-chat__char-count--over': isOverLimit }"
            >{{ charsRemaining }}</span
          >
          <q-btn
            flat
            dense
            round
            icon="send"
            color="primary"
            :disable="!draft.trim() || sending || isOverLimit"
            :loading="sending"
            @click="send"
          />
        </template>
      </q-input>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

/**
 * Shape returned by GET /chat/messages and pushed via
 * the SignalR `ReceiveChatMessage` event.
 *
 * The `userName` field is resolved server-side from the Users table
 * using the `userId` foreign key, since the user's identity comes
 * from Auth0 where the username is their email address.
 */
export interface IChatMessage {
  /** Server-assigned unique id (GroupChatMessageId) */
  id: number;
  /** Auth0 user id of the author (email) */
  userId: string;
  /** Display name resolved from Users.Name */
  userName: string;
  /** Message body */
  text: string;
  /** ISO-8601 UTC timestamp */
  timestamp: string;
}

/** Maximum message length — matches the NVARCHAR(2000) backend constraint. */
const MAX_MESSAGE_LENGTH = 2000;

export default defineComponent({
  name: 'GroupChat',

  emits: ['update:expanded'],

  data() {
    return {
      /** 'collapsed' = header only, 'mini' = 300px, 'expanded' = full drawer */
      viewState: 'collapsed' as 'collapsed' | 'mini' | 'expanded',
      draft: '',
      unreadCount: 0,
      /** true when user has scrolled up — prevents auto-scroll hijacking */
      userScrolledUp: false,
      /** tracks previous message count to detect new arrivals */
      previousMessageCount: 0,
      /** prevents flooding typing signals — throttle gate */
      typingThrottled: false,
      /** drives the title text flash on incoming message */
      newMessageFlash: false,
      newMessageFlashTimer: null as ReturnType<typeof setTimeout> | null,
      /** gates animation so it doesn't fire on initial fetch */
      initialLoadComplete: false,
    };
  },

  computed: {
    currentUserId(): string {
      return this.$store.state.user?.id ?? '';
    },
    messages(): IChatMessage[] {
      return this.$store.state.groupChat.messages;
    },
    loading(): boolean {
      return this.$store.state.groupChat.loading;
    },
    loadingOlder(): boolean {
      return this.$store.state.groupChat.loadingOlder;
    },
    sending(): boolean {
      return this.$store.state.groupChat.sending;
    },
    hasMoreMessages(): boolean {
      return this.$store.state.groupChat.hasMoreMessages;
    },
    someoneTyping(): boolean {
      return this.$store.state.groupChat.someoneTyping;
    },
    groupName(): string {
      return this.$store.state.groupChat.groupName;
    },
    chatTitle(): string {
      const name = this.groupName;
      if (!name) return 'Chat';
      const truncated = name.length > 25 ? name.substring(0, 25) + '…' : name;
      return `${truncated} Chat`;
    },
    charsRemaining(): number {
      return MAX_MESSAGE_LENGTH - this.draft.length;
    },
    isOverLimit(): boolean {
      return this.draft.length > MAX_MESSAGE_LENGTH;
    },
    expanded(): boolean {
      return this.viewState === 'expanded';
    },
    viewClass(): Record<string, boolean> {
      return {
        'group-chat--collapsed': this.viewState === 'collapsed',
        'group-chat--mini': this.viewState === 'mini',
        'group-chat--expanded': this.viewState === 'expanded',
      };
    },
    headerIcon(): string {
      switch (this.viewState) {
        case 'collapsed':
          return 'expand_less';
        case 'mini':
          return 'open_in_full';
        case 'expanded':
          return 'close_fullscreen';
        default:
          return 'expand_less';
      }
    },
  },

  watch: {
    viewState(val: string) {
      if (val === 'expanded') {
        this.unreadCount = 0;
        this.$nextTick(() => this.scrollToBottom());
      } else if (val === 'mini') {
        this.unreadCount = 0;
        this.$nextTick(() => this.scrollToBottom());
      }
      this.$emit('update:expanded', val === 'expanded');
    },
    // React to new messages arriving via the store (from SignalR or send).
    messages(newMessages: IChatMessage[], oldMessages: IChatMessage[]) {
      if (newMessages.length > this.previousMessageCount) {
        // Only flash for incoming SignalR messages, not initial fetch or own sends.
        const newest = newMessages[newMessages.length - 1];
        if (
          this.initialLoadComplete &&
          newest &&
          newest.userId !== this.currentUserId
        ) {
          this.triggerNewMessageFlash();
        }
        if (this.viewState !== 'collapsed' && !this.userScrolledUp) {
          this.$nextTick(() => this.scrollToBottom());
        } else if (this.viewState === 'collapsed') {
          if (this.initialLoadComplete) {
            this.unreadCount += newMessages.length - this.previousMessageCount;
          }
        }
      }
      this.previousMessageCount = newMessages.length;
    },
    someoneTyping(val: boolean) {
      if (val && this.viewState !== 'collapsed' && !this.userScrolledUp) {
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    draft(val: string) {
      if (val.length > 0 && !this.typingThrottled) {
        this.typingThrottled = true;
        this.$store.dispatch('sendChatTyping');
        setTimeout(() => {
          this.typingThrottled = false;
        }, 2000);
      }
    },
  },

  mounted() {
    this.$store.dispatch('fetchChatMessages').then(() => {
      this.previousMessageCount = this.messages.length;
      this.initialLoadComplete = true;
      this.$nextTick(() => this.scrollToBottom());
    });
  },

  methods: {
    // ── UI handlers ────────────────────────────────────────────
    cycleState() {
      const order: Array<'collapsed' | 'mini' | 'expanded'> = [
        'collapsed',
        'mini',
        'expanded',
      ];
      const idx = order.indexOf(this.viewState);
      this.viewState = order[(idx + 1) % order.length];
    },

    formatTime(iso: string): string {
      const d = new Date(iso);
      return (
        d.toLocaleString([], {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: 'UTC',
        }) + ' UTC'
      );
    },

    scrollToBottom() {
      const el = this.$refs.messageList as HTMLElement | undefined;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },

    onScroll() {
      const el = this.$refs.messageList as HTMLElement | undefined;
      if (!el) return;

      // Trigger pagination when scrolled near the top.
      if (el.scrollTop < 50 && this.hasMoreMessages && !this.loadingOlder) {
        this.loadOlderMessages();
      }

      // Consider "at bottom" if within 40px of the end.
      this.userScrolledUp =
        el.scrollTop + el.clientHeight < el.scrollHeight - 40;
    },

    loadOlderMessages() {
      const el = this.$refs.messageList as HTMLElement | undefined;
      const previousScrollHeight = el?.scrollHeight ?? 0;

      this.$store.dispatch('loadOlderChatMessages').then(() => {
        // Preserve scroll position so older messages don't push the view down.
        this.$nextTick(() => {
          if (el) {
            el.scrollTop = el.scrollHeight - previousScrollHeight;
          }
        });
      });
    },

    async send() {
      const text = this.draft.trim();
      if (!text || this.sending || this.isOverLimit) return;

      this.draft = '';
      await this.$store.dispatch('sendChatMessage', text);
      this.$nextTick(() => this.scrollToBottom());
    },

    deleteMessage(id: number) {
      this.$store.dispatch('deleteChatMessage', id);
    },

    triggerNewMessageFlash() {
      if (this.newMessageFlashTimer) clearTimeout(this.newMessageFlashTimer);
      this.newMessageFlash = false;
      // Force a reflow so the animation restarts if already playing.
      void (this.$el as HTMLElement)?.offsetWidth;
      this.newMessageFlash = true;
      this.newMessageFlashTimer = setTimeout(() => {
        this.newMessageFlash = false;
        this.newMessageFlashTimer = null;
      }, 2000);
    },
  },
});
</script>

<style scoped>
.group-chat {
  display: flex;
  flex-direction: column;
  border-top: 1px solid #ddd;
  background: #fff;
  margin-top: auto;
  flex-shrink: 0;
}

.group-chat__title--flash {
  animation: chat-title-flash 2s ease-out forwards;
}

@keyframes chat-title-flash {
  0% {
    color: #ff0080;
    text-shadow: 0 0 6px rgba(255, 0, 128, 0.7);
  }
  16% {
    color: #ff6600;
    text-shadow: 0 0 6px rgba(255, 102, 0, 0.7);
  }
  33% {
    color: #f5d800;
    text-shadow: 0 0 6px rgba(245, 216, 0, 0.7);
  }
  50% {
    color: #00cc55;
    text-shadow: 0 0 6px rgba(0, 204, 85, 0.7);
  }
  66% {
    color: #0099ff;
    text-shadow: 0 0 6px rgba(0, 153, 255, 0.7);
  }
  83% {
    color: #9900ff;
    text-shadow: 0 0 6px rgba(153, 0, 255, 0.7);
  }
  100% {
    color: #fff;
    text-shadow: none;
  }
}

/* Collapsed — header bar only */
.group-chat--collapsed {
  height: auto;
}

/* Mini — fixed compact height */
.group-chat--mini {
  height: 300px;
}

/* Expanded — take all remaining space in the drawer */
.group-chat--expanded {
  height: auto;
  flex: 1 1 0;
  min-height: 0;
}

.group-chat__header {
  position: relative;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #24337a;
  color: #fff;
  cursor: pointer;
  flex-shrink: 0;
  user-select: none;
  min-height: 42px;
  box-sizing: border-box;
}

.group-chat__title {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

/* ── Message list ─────────────────────────────────── */
.group-chat__messages {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #f5f5f5;
  -webkit-overflow-scrolling: touch;
}

/* ── Bubble ───────────────────────────────────────── */
.group-chat__bubble {
  max-width: 85%;
  padding: 6px 10px;
  border-radius: 10px;
  background: #e3e8f0;
  align-self: flex-start;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.group-chat__bubble--own {
  background: #d1e7dd;
  align-self: flex-end;
}

.group-chat__meta {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 2px;
}

.group-chat__author {
  font-size: 11px;
  font-weight: 600;
  color: #24337a;
}

.group-chat__bubble--own .group-chat__author {
  color: #0f5132;
}

.group-chat__time {
  font-size: 10px;
  color: #888;
}

.group-chat__text {
  font-size: 13px;
  line-height: 1.35;
  color: #222;
}

/* ── Input area ───────────────────────────────────── */
.group-chat__input {
  padding: 6px 8px;
  border-top: 1px solid #ddd;
  background: #fff;
  flex-shrink: 0;
}

.group-chat__field {
  font-size: 13px;
}

.group-chat__loading {
  text-align: center;
  font-size: 11px;
  color: #999;
  padding: 6px 0;
}

.group-chat__delete {
  opacity: 0;
  transition: opacity 0.15s;
  margin-left: auto;
}

.group-chat__bubble:hover .group-chat__delete {
  opacity: 1;
}

.group-chat__char-count {
  font-size: 11px;
  color: #999;
  margin-right: 4px;
  line-height: 1;
}

.group-chat__char-count--over {
  color: #c10015;
  font-weight: 600;
}

/* ── Viewer notice banner ─────────────────────────── */
.group-chat__banner {
  padding: 6px 12px;
  background: #ff9800;
  color: #fff;
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  flex-shrink: 0;
}

/* ── Empty state ──────────────────────────────────── */
.group-chat__empty {
  text-align: center;
  font-size: 12px;
  color: #999;
  padding: 24px 10px;
  font-style: italic;
}

/* ── Typing indicator ─────────────────────────────── */
.group-chat__typing {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 12px;
  color: #666;
  font-style: italic;
}

.typing-dots {
  display: inline-flex;
  gap: 3px;
}

.typing-dots span {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #888;
  animation: typing-bounce 1.4s ease-in-out infinite;
}

.typing-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing-bounce {
  0%,
  60%,
  100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-4px);
  }
}
</style>
